import { invoicePaymentForGrid } from '../../../spd/components/InvoicePayment/InvoicePaymentsSearch';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import {
  CreateUpdateInvoicePaymentDto,
  CreateUpdateInvoicePaymentLinkDto,
  DefaultApi,
  GetInvoicePaymentDto,
  GetInvoicePaymentLinkDto,
  InvoicePaymentControllerImportCsv200Response,
  SaveLinksDto,
} from '../autogenerated/spdApiClient';
import { validateAgainst } from '../validation/validateAgainst';
import { InvoicePaymentSchema } from '../validation/schemas';

export interface InvoicePaymentsContextInterface {
  getById: (id: string) => Promise<GetInvoicePaymentDto>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    collectionId?: string,
    onlyWithUnlinkedAmount?: boolean,
    onlyFullyLinked?: boolean,
    companyId?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (
    filterText?: string,
    filterColumns?: string,
    collectionId?: string,
    onlyWithUnlinkedAmount?: boolean,
    onlyFullyLinked?: boolean,
    companyId?: string,
  ) => Promise<number>;
  create: (dto: CreateUpdateInvoicePaymentDto) => Promise<GetInvoicePaymentDto>;
  createSingle: (
    dto: CreateUpdateInvoicePaymentDto,
  ) => Promise<GetInvoicePaymentDto>;
  update: (
    id: string,
    dto: CreateUpdateInvoicePaymentDto,
  ) => Promise<GetInvoicePaymentDto>;
  createLink: (
    dto: CreateUpdateInvoicePaymentLinkDto,
  ) => Promise<GetInvoicePaymentLinkDto>;
  getLinksByPaymentId: (
    paymentId: string,
  ) => Promise<GetInvoicePaymentLinkDto[]>;
  getLinksByInvoiceId: (
    invoiceId: string,
  ) => Promise<GetInvoicePaymentLinkDto[]>;
  deleteLink: (id: string) => Promise<void>;
  saveLinks: (
    invoicePaymentId: string,
    dto: SaveLinksDto,
  ) => Promise<GetInvoicePaymentLinkDto[]>;
  importCsv: (
    file: File,
    collectionName: string,
  ) => Promise<InvoicePaymentControllerImportCsv200Response>;
}

export const InvoicePaymentsContext = (
  api: DefaultApi,
): InvoicePaymentsContextInterface => ({
  getById: (id: string) => api.invoicePaymentControllerGet(id),

  async getAllByPortion(
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    collectionId?: string,
    onlyWithUnlinkedAmount?: boolean,
    onlyFullyLinked?: boolean,
    companyId?: string,
  ) {
    const response = await api.invoicePaymentControllerGetAllByPortion(
      pageSize,
      pageNumber,
      orderBy || '',
      orderDirection || '',
      filterText || '',
      filterColumns,
      collectionId,
      onlyWithUnlinkedAmount?.toString(),
      onlyFullyLinked?.toString(),
      companyId,
    );
    return response.map(invoicePaymentForGrid);
  },

  async getAllCount(
    filterText?: string,
    filterColumns?: string,
    collectionId?: string,
    onlyWithUnlinkedAmount?: boolean,
    onlyFullyLinked?: boolean,
    companyId?: string,
  ) {
    return api.invoicePaymentControllerGetAllCount(
      filterText || '',
      filterColumns,
      collectionId,
      onlyWithUnlinkedAmount?.toString(),
      onlyFullyLinked?.toString(),
      companyId,
    );
  },

  create: (dto: CreateUpdateInvoicePaymentDto) =>
    api.invoicePaymentControllerCreate(dto),

  createSingle: (dto: CreateUpdateInvoicePaymentDto) =>
    api.invoicePaymentControllerCreateSingle(dto),

  update: (id: string, dto: CreateUpdateInvoicePaymentDto) =>
    api.invoicePaymentControllerUpdate(id, dto),

  createLink: (dto: CreateUpdateInvoicePaymentLinkDto) =>
    api.invoicePaymentControllerCreateLink(dto),

  getLinksByPaymentId: (paymentId: string) =>
    api.invoicePaymentControllerGetLinksByPaymentId(paymentId),

  getLinksByInvoiceId: (invoiceId: string) =>
    api.invoicePaymentControllerGetLinksByInvoiceId(invoiceId),

  deleteLink: (id: string) => api.invoicePaymentControllerDeleteLink(id),

  saveLinks: (invoicePaymentId: string, dto: SaveLinksDto) =>
    api.invoicePaymentControllerSaveLinks(invoicePaymentId, dto),

  importCsv: (file: File, collectionName: string) =>
    api.invoicePaymentControllerImportCsv(file, collectionName),
});

export const newInvoicePayment = (): CreateUpdateInvoicePaymentDto => ({
  title: null,
  amount: null,
  paymentDate: null,
  paymentCollectionId: null,
  sender: null,
});

export const convertInvoicePayment = (
  dto: GetInvoicePaymentDto,
): CreateUpdateInvoicePaymentDto => ({
  title: dto.title ?? null,
  amount: dto.amount ?? null,
  paymentDate: dto.paymentDate ?? null,
  paymentCollectionId: dto.paymentCollection?.id ?? null,
  sender: dto.sender ?? null,
});

export const validate = (dto: CreateUpdateInvoicePaymentDto) =>
  validateAgainst(InvoicePaymentSchema, dto);
